import { SendEventDtoType } from "@cede/types";
import { MetricsEventsQueue } from "./metrics-events-queue";
import { serializedBytes, takeUnloadBatch } from "./metrics-unload-batch";

export type MetricsIdentityStamp = {
  /** Stable transport identity; a change isolates the previous in-flight batch. */
  key: string;
  /** Per-event map, so the stamped batch always matches the queued one index for index. */
  stamp: (event: SendEventDtoType) => SendEventDtoType;
};

/** Every path where queued events do not reach the backend. */
export type MetricsUndeliveredReason =
  | "identity-changed"
  | "batch-rejected"
  | "identity-unavailable"
  | "unload-truncated"
  | "unload-send-failed";

/**
 * Which lifecycle event drove an unload send. Only `pagehide` is terminal: on
 * `visibilitychange` the page survives, so a failed batch is still queued and the
 * next ordinary flush carries it. Reporting the two alike overstates the loss.
 */
export type MetricsUnloadTrigger = "pagehide" | "visibilitychange";

/** Why a send rejected. `network` covers a browser-refused keepalive as well as a dead connection. */
export type MetricsSendFailureCause = "timeout" | "http-error" | "network" | "app-rejected";

/**
 * The reasons a person can do something about. The other two are outcomes, not faults:
 * `unload-send-failed` is a connection dying on a page that is already closing, and
 * `identity-changed` is the queue refusing to file one user's events under another's.
 * Alerting on either only ever produced an issue nobody could close, which buried the
 * three below. Rates belong in Mixpanel delivery volume, not in an error tracker.
 */
export const ACTIONABLE_UNDELIVERED_REASONS: ReadonlySet<MetricsUndeliveredReason> = new Set([
  "batch-rejected",
  "unload-truncated",
  "identity-unavailable",
]);

export type MetricsUndeliveredInfo = {
  reason: MetricsUndeliveredReason;
  count: number;
  /** Unload-path reasons only. */
  trigger?: MetricsUnloadTrigger;
  /** `unload-send-failed` only. */
  cause?: MetricsSendFailureCause;
  status?: number;
  /** Serialized size of the attempted keepalive body, against the 48 KB budget. */
  bytes?: number;
};

export type MetricsFlushDeps = {
  /** Sends one batch. Must reject on failure so the caller can retry. */
  send: (events: SendEventDtoType[], options?: { keepalive?: boolean }) => Promise<unknown>;
  /**
   * Resolved per flush, never memoised across renders: identity can appear after the
   * transport rendered (consent acceptance starts Mixpanel and mints the device id).
   * `null` leaves the events queued.
   */
  resolveIdentity: () => MetricsIdentityStamp | null;
  /**
   * Last chance for the unload path only, where leaving events queued loses them.
   * Consent started Mixpanel there by construction, so the device id exists; Mixpanel
   * merges a batch sent under it onto the user once a later identified event carries
   * the same `$device_id`. Anonymous on purpose: a stored `$user_id` names whoever
   * identified on this device last, which is not necessarily whoever is signed in now,
   * and no merge undoes a batch written to the wrong profile. Never used by the
   * ordinary flush, which can afford to wait for the real one.
   */
  resolveAnonymousIdentity?: () => MetricsIdentityStamp | null;
  /** A batch the backend will never accept is dropped rather than retried forever. */
  isPermanentFailure?: (error: unknown) => boolean;
  /** No drop is silent: every undelivered batch is reported with its size. */
  onUndelivered?: (info: MetricsUndeliveredInfo) => void;
};

/** "Come back later", not "this batch is malformed" — dropping these would lose deliverable events. */
const RETRYABLE_4XX = new Set([408, 429]);

/**
 * `ApiHelper` throws two shapes: a `ServerApiError` carries `statusCode` on the error,
 * an `Rfc9457Error` carries it at `details.status`. The API's Zod rejection — the case
 * this classifier exists for — is the latter, so reading only the former misses it.
 */
function readErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const candidate = error as { statusCode?: unknown; status?: unknown; details?: unknown };
  const nested =
    candidate.details && typeof candidate.details === "object"
      ? (candidate.details as { status?: unknown }).status
      : undefined;
  const status = candidate.statusCode ?? candidate.status ?? nested;
  return typeof status === "number" ? status : undefined;
}

/**
 * Splits the one thing the unload path could not previously tell apart: a connection
 * that died with the page from an API that refused the batch. `network` also covers a
 * keepalive the browser rejected outright, which the reported `bytes` distinguishes.
 */
export function classifySendFailure(error: unknown): MetricsSendFailureCause {
  const status = readErrorStatus(error);
  if (status !== undefined) return "http-error";
  // The app's own "200 but success:false" throw is a plain Error with no transport shape.
  if (!error || typeof error !== "object") return "app-rejected";
  if ((error as { isTimeout?: unknown }).isTimeout === true) return "timeout";
  return (error as { name?: unknown }).name === "ApiRequestError" ? "network" : "app-rejected";
}

/** 4xx means the batch itself is unacceptable; retrying it would block everything behind it. */
export function isPermanentSendFailure(error: unknown): boolean {
  const status = readErrorStatus(error);
  if (status === undefined) return false;
  return status >= 400 && status < 500 && !RETRYABLE_4XX.has(status);
}

/** Sends all events available for the current identity, restoring them on failure. */
export async function flushMetricsQueue(queue: MetricsEventsQueue, deps: MetricsFlushDeps): Promise<void> {
  // Resolving identity is not free of side effects: the apps report a stall from it.
  // An empty queue must return before that runs, not after. Skipping alignIdentity
  // here is safe: an identity change with nothing queued is isolated by the apps'
  // invalidate(), and the anonymous-to-identified handshake keeps its events on purpose.
  if (queue.pendingCount === 0) return;

  const identity = deps.resolveIdentity();
  if (!identity) return;

  const flushGeneration = queue.alignIdentity(identity.key);

  while (queue.generation === flushGeneration) {
    // Whatever a last-chance send delivered is acknowledged before this looks.
    if (queue.pendingLastChanceSend) {
      await queue.pendingLastChanceSend;
      if (queue.generation !== flushGeneration) return;
    }
    const processing = queue.beginProcessing();
    if (!processing) return;

    try {
      await deps.send(processing.events.map(identity.stamp));
      queue.completeProcessing(processing.generation);
    } catch (error) {
      if (deps.isPermanentFailure?.(error)) {
        // Restoring a batch the backend will never accept blocks every event behind it.
        queue.completeProcessing(processing.generation);
        deps.onUndelivered?.({ reason: "batch-rejected", count: processing.events.length });
      } else {
        const droppedCount = queue.restoreProcessing(processing.generation);
        if (droppedCount > 0) deps.onUndelivered?.({ reason: "identity-changed", count: droppedCount });
        throw error;
      }
    }

    // A transport configured for the old identity must never drain the new one.
    if (queue.generation !== processing.generation) return;
  }
}

/**
 * Best-effort keepalive send that removes events only after transport acknowledgement.
 *
 * `trigger` is reported, not acted on: a `visibilitychange` failure leaves the batch
 * queued for the next ordinary flush, so it is not the loss a `pagehide` failure is.
 */
export function flushMetricsQueueOnUnload(
  queue: MetricsEventsQueue,
  deps: MetricsFlushDeps,
  trigger?: MetricsUnloadTrigger,
): void {
  // Before resolving identity, for the same reason as the ordinary flush.
  if (queue.currentGenerationSnapshot().events.length === 0) return;

  const identity = deps.resolveIdentity();
  // Only a resolved identity may move the generation: aligning to the anonymous one
  // would isolate the very batch in flight that this send exists to carry.
  if (identity) queue.alignIdentity(identity.key);

  // An in-flight batch already has a transport carrying it under the resolved identity,
  // so the keepalive copy only insures it against the page dying first. Under the
  // anonymous identity that copy is not insurance but a second distinct_id holding the
  // same `$insert_id`, which Mixpanel's dedup does not span, so it would count twice.
  const pending = queue.currentGenerationSnapshot({ includeInFlight: identity != null });
  if (pending.events.length === 0) return;

  const sendIdentity = identity ?? deps.resolveAnonymousIdentity?.();
  if (!sendIdentity) {
    // Neither an attributed nor a device identity, so there is nothing to send under.
    // A hidden page keeps the batch queued for the next ordinary flush, so only a closing one loses it.
    if (trigger !== "visibilitychange") {
      deps.onUndelivered?.({ reason: "identity-unavailable", count: pending.events.length, trigger });
    }
    return;
  }

  const stamped = pending.events.map(sendIdentity.stamp);
  const { indices, deferredCount } = takeUnloadBatch(stamped);
  if (deferredCount > 0) deps.onUndelivered?.({ reason: "unload-truncated", count: deferredCount, trigger });
  if (indices.length === 0) return;

  const sourceBatch = indices.map((index) => pending.events[index]!);
  const body = indices.map((index) => stamped[index]!);
  const settled = deps
    .send(body, { keepalive: true })
    .then(() => queue.acknowledge(pending.generation, sourceBatch))
    .catch((error: unknown) =>
      deps.onUndelivered?.({
        reason: "unload-send-failed",
        count: sourceBatch.length,
        trigger,
        cause: classifySendFailure(error),
        status: readErrorStatus(error),
        bytes: serializedBytes(body),
      }),
    );
  queue.holdForLastChanceSend(settled);
}

/**
 * Registers the lifecycle flush both apps need and returns the unsubscribe.
 *
 * `visibilitychange` is not redundant with `pagehide`: on mobile a backgrounded tab
 * can be discarded without ever firing `pagehide`. Returning `null` from `resolveDeps`
 * skips the send, which is how a consent refusal is expressed.
 */
export function attachMetricsUnloadFlush(
  queue: MetricsEventsQueue,
  resolveDeps: () => MetricsFlushDeps | null,
): () => void {
  const flushOnUnload = (trigger: MetricsUnloadTrigger) => {
    const deps = resolveDeps();
    if (deps) flushMetricsQueueOnUnload(queue, deps, trigger);
  };
  const onPageHide = () => flushOnUnload("pagehide");
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") flushOnUnload("visibilitychange");
  };

  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
